import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, DollarSign } from 'lucide-react';
import { useFundingStore } from '../../store/useFundingStore';
import { useAppStore } from '../../store/useAppStore';
import CreateFundingModal from './CreateFundingModal';
import type { FundingStage } from '../../types';

const STAGE_COLORS: Record<FundingStage, string> = {
  prospect: '#94a3b8',
  qualifying: '#3b82f6',
  preparing: '#8b5cf6',
  submitted: '#f59e0b',
  'under-review': '#f97316',
  awarded: '#22c55e',
  declined: '#ef4444',
  withdrawn: '#6b7280',
};

const STAGE_LABELS: Record<FundingStage, string> = {
  prospect: 'Prospect',
  qualifying: 'Qualifying',
  preparing: 'Preparing',
  submitted: 'Submitted',
  'under-review': 'Under Review',
  awarded: 'Awarded',
  declined: 'Declined',
  withdrawn: 'Withdrawn',
};

function formatCurrency(amount: number, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export default function FundingPanel() {
  const { opportunities } = useFundingStore();
  const { setActivePanel } = useAppStore();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [stageFilter, setStageFilter] = useState<string>('all');

  const filtered = opportunities.filter((o) => {
    const matchesSearch =
      o.name.toLowerCase().includes(search.toLowerCase()) ||
      o.funderName.toLowerCase().includes(search.toLowerCase());
    const matchesStage = stageFilter === 'all' || o.stage === stageFilter;
    return matchesSearch && matchesStage;
  });

  const handleSelect = (id: string) => {
    setActivePanel(null);
    navigate(`/funding/${id}`);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Search + New */}
      <div className="p-3 space-y-2">
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 w-full px-3 py-2 bg-accent text-white rounded-lg text-sm font-medium hover:bg-accent/90 transition-colors"
        >
          <Plus size={16} />
          New Opportunity
        </button>

        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search funding..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
          />
        </div>

        <select
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
          className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-1 focus:ring-accent"
        >
          <option value="all">All Stages</option>
          {Object.entries(STAGE_LABELS).map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>
      </div>

      {/* Opportunity List */}
      <div className="flex-1 overflow-y-auto px-2">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400 dark:text-gray-500">
            <DollarSign size={32} className="mb-2" />
            <p className="text-sm">No opportunities found</p>
          </div>
        ) : (
          <div className="space-y-0.5">
            {filtered.map((opp) => (
              <button
                key={opp._id}
                onClick={() => handleSelect(opp._id)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-left transition-colors group"
              >
                <div
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: STAGE_COLORS[opp.stage] || '#94a3b8' }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {opp.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {opp.funderName} &middot; {STAGE_LABELS[opp.stage] || opp.stage}
                  </p>
                </div>
                {opp.amount > 0 && (
                  <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                    {formatCurrency(opp.amount, opp.currency)}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateFundingModal onClose={() => setShowCreate(false)} />
      )}
    </div>
  );
}
